import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { ErrorReportService } from './error-report.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GenericDialogComponent } from 'src/components/generic-dialog/generic-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';

declare global {
  interface Window {
    OneSignal: any;
    OneSignalDeferred: any[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class OneSignalService {
  private OneSignal: any;
  private isInitialized: boolean = false; 
  public onesignalId: string | null = null;
  public externalId: string | null = null;
  public signId: string | null = null;
  public token: string | null = null;
  private isPromptOpen: boolean = false;
  private promptLock: boolean = false;

  private notificationsSource = new BehaviorSubject<any[]>([]);
  notifications$ = this.notificationsSource.asObservable();

  private alertsSource = new BehaviorSubject<any[]>([]);
  alerts$ = this.alertsSource.asObservable();

  constructor(
    private http: HttpService,
    private errorReport: ErrorReportService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  public initOneSignal() {
    window.OneSignalDeferred = window.OneSignalDeferred || [];

    window.OneSignalDeferred.push(async (OneSignal: any) => {
      if (this.isInitialized) {
        console.warn('OneSignal SDK já foi inicializado.');
        return; // Evita nova inicialização
      }

      try {

        this.OneSignal = OneSignal;
        await OneSignal.init({
          appId: '642c312c-6678-41c7-b87f-700eab57490d',
          allowLocalhostAsSecureOrigin: true
        });

        this.checkNotificationPermission();
        
        
        this.isInitialized = true; // Marca como inicializado
        
        this.OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
          //console.log('Notificação recebida:', event);
          this.listNotification();
          this.listAlerts();
        });

      } catch (error) {
        console.error('Erro ao iniciar o OneSignal:', error);
      }
    });
  }

  public checkNotificationPermission(){
    // Verificar a permissão de notificações
    const permission = Notification.permission;
    //console.log('PERMISSION:', permission);

    // Exibe o prompt OneSignal se a permissão for "default"
    if (permission === 'default') {
        if(this.OneSignal.User.onesignalId){
          //console.log('está desabilitado a notificação mas tem cadastro no onsignal');
          this.onesignalId = this.OneSignal.User.onesignalId;
          Notification.requestPermission();
          this.snackbar.open('Por Favor, permita as notificações para ficar sempre por dentro das novidades!', 'OK', { duration: 5000 })
        }else{
          if (!this.isPromptOpen && !this.promptLock) {
            //console.log('Tentando exibir o prompt do OneSignal...');
            this.isPromptOpen = true;  // Define o prompt como aberto
            this.promptLock = true;    // Ativa o lock

            //console.log('abriu prompt')
            this.OneSignal.Slidedown.promptPush({ force: true });
          
          }

          this.OneSignal.Slidedown.addEventListener('slidedownClosed', () => {
            //console.log('Slidedown Prompt foi fechado.');
            this.isPromptOpen = false; // Libera após o prompt ser fechado
            this.promptLock = false;   // Libera o lock
          });
        }
    } else if (permission === 'granted') {
      //console.log('Usuário já aceitou as notificações.');
      if(this.OneSignal.User.onesignalId){
        this.onesignalId = this.OneSignal.User.onesignalId;
        if(this.signId == null){
          this.fetchUserAndSubscription();
          //console.log('registrando IDs:', this.onesignalId);
        }
      }else{
        this.OneSignal.Slidedown.promptPush({force:true}); // Mostra o slide-down prompt manualmente
      }
    } else if (permission === 'denied') {
      //console.log('Usuário recusou as notificações.');
      this.PermissionDenied();
    }

    this.OneSignal.User.addEventListener('change', (event: any) => {
      // console.log('user:', event);
      this.onesignalId = event.current.onesignalId; // Atualiza o userId
      this.externalId = event.current.externalId ? event.current.externalId : null;
    });

    this.OneSignal.User.PushSubscription.addEventListener("change", (event: any) => {
      // console.log('subs:', event);
      this.signId = event.current.id;
      this.token = event.current.token;
      this.saveOneSignalUserIfReady();
    });

    //console.log('onesignalID', this.onesignalId);

  }

  private fetchUserAndSubscription() {
      this.onesignalId = this.OneSignal.User.onesignalId;
      this.externalId = this.OneSignal.User.externalId ? this.OneSignal.User.externalId : null;     
      this.signId = this.OneSignal.User.PushSubscription.id;      ;
      this.token = this.OneSignal.User.PushSubscription.token;
      this.saveOneSignalUserIfReady();
  }

  public PermissionDenied() {
    const permission = Notification.permission;
    if (permission === 'denied') {
      
      if (this.dialog.openDialogs.length > 0) {
        return; // Não abre um novo diálogo
      }

      let options = {};

      let icon = 'error';
      let icon_class = 'font-icon';
      let message = '';
      let message2 = '';
      let buttons = [];
      
      message = `Notificações estão desativadas`;
      message2 = `
      <div class="text-left">
        Por favor, é muito importante que ative-as nas configurações do navegador. <br>
        Siga o passo a passo abaixo: <br>
        1 - Vá para <strong>Configurações</strong>.<br>
        2 - No menu à esquerda, clique em <strong>Privacidade e segurança</strong>.<br>
        3 - Vá até <strong>Configurações de sites</strong>.<br>
        4 - Role até <strong>Notificações</strong> na seção de <strong>Permissões</strong>.<br>
        5 - Uma lista de sites com permissões para notificações será exibida.<br> 
        6 - Selecione o site da <strong>Colloca</strong> na lista e clique em <strong>Permitir</strong> notificações.
      </div>`;

      buttons = [
        { name: 'Fechar', value: 1, class: 'danger' }
      ];

      options = {
        disableClose: true,
        data: {
          icon,
          icon_class,
          message,
          message2,
          buttons
        }
      };

      this.dialog.open(GenericDialogComponent, options)
        .afterClosed()
        .subscribe(
          result => {
            if (result == 1) {
              //console.log('resultado');
            }

          }
        );

      }

  }

  private saveOneSignalUserIfReady() {
    if (this.onesignalId && this.signId && this.token) {
      //console.log('salvando dados');
      this.saveOneSignalUser(); // Chama a função principal quando os dados estão prontos
    }
  }

  private saveOneSignalUser() {
    const user = JSON.parse(localStorage.getItem('token'));
    const data = {
      lead_id: user.id,
      onesignal_id: this.onesignalId,
      external_id: this.externalId,
      signature_id: this.signId,
      token: this.token
    };

    this.http.save('onsignal:saveUser', { data: data })
      .subscribe(
        (result: any) => {
          if (result.type === 'OK') {
            //console.log('Usuario inserido');
          }
        },
        err => this.errorReport.showMessage(err)
      )
  }  

  sendNotification(userId: string[], title: string, message: string, notification_for_all: string): Observable<any> {
    return this.http.save('onsignal:sendNotification', { id: userId, title: title, message: message, notification_for_all: notification_for_all });
  }

  listNotification() {
    const user = JSON.parse(localStorage.getItem('token'));
    if (user && user.id) {
    this.http.save('onsignal:listNotification', { id: user.id })
      .subscribe(
        (result: any) => {
          if (result.type === 'OK') {
            const notifications = result.data.map(notification => ({
              id: notification.id,
              logo: 'assets/images/icon.png',
              title: notification.title,
              message: notification.message,
              data: notification.data,
              time: this.calculateTimeSince(notification.data),
              unread: notification.unread === 'true',
              footerIcon: 'assets/images/icon2.png',
              footerText: 'Atualização'
            }));
            
            // Update the BehaviorSubject with new notifications
            this.notificationsSource.next(notifications);
          }
        },
        err => this.errorReport.showMessage(err)
      );
    }  
  }

  calculateTimeSince(dateString: string): string {
    const notificationDate = new Date(dateString);
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - notificationDate.getTime();
  
    const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
    if (daysDifference > 0) {
      return `${daysDifference} dia${daysDifference > 1 ? 's' : ''}`;
    }
  
    const hoursDifference = Math.floor(timeDifference / (1000 * 3600));
    if (hoursDifference > 0) {
      return `${hoursDifference} hora${hoursDifference > 1 ? 's' : ''}`;
    }else{
      return `Hoje`;
    }
  }

  listMessage(id: number, name_candidate?: string, process_name?: string, teste_name?: string, date: string | null = null, meet_link: string | null = null): string {
    // Crie um objeto ou mapeamento para associar IDs com mensagens
    const messages = {
      1: `O(a) candidato(a) ${name_candidate}, inscreveu-se recentemente no processo seletivo ${process_name}.`,
      2: `Você foi contrato no processo seletivo ${process_name}.`,
      3: `O(a) candidato(a) ${name_candidate}, confirmou sua participação na entrevista para o processo seletivo ${process_name}.`,
      4: `O(a) candidato(a) ${name_candidate}, informou que não participará da entrevista para o processo seletivo ${process_name}.`,
      5: `Você tem uma entrevista agendada para o processo seletivo ${process_name}.`,
      6: `O(a) candidato(a) ${name_candidate}, respondeu o teste para o processo seletivo ${process_name}.`,
      7: `Você tem um ou mais testes ('${teste_name}') pendente para responder no processo seletivo ${process_name}.`,
      8: `O Teste ('${teste_name}') foi avaliado, favor verificar no processo seletivo ${process_name}.`,
      9: `Você tem uma entrevista agendada para o processo seletivo ${process_name}. Para participar da reunião online no dia ${date}, acesse o link do Google Meet: ${meet_link}. Prepare-se e boa sorte!`,
      // Adicione outros IDs e mensagens aqui conforme necessário
    };
  
    // Verifica se o ID fornecido existe no objeto de mensagens
    const baseMessage = messages[id];
    return baseMessage;

  }

  listAlerts() {
    const alerts = [{
      value: true
    }];

    this.alertsSource.next(alerts);
  }

}
